import Box from '@mui/material/Box'

import { loadedFullNameLogoLatte } from '$assets'

import AppMenuFlyout from './app-menu-flyout'
import TopbarMobileButton from './TopbarMobileButton'
import TopbarModuleNav from './TopbarModuleNav'
import VenueSwitcher from './venue-switcher'
import { isMobile } from '$utils'

/**
 * The overall app topbar - a.k.a. the top or main nav.
 *
 * The layout has two regions:
 *  - a LEFT region with the Loaded logo and the main module navigation
 *    (`TopbarNav`), and
 *  - a RIGHT region with the action slots (settings, venue switcher, profile).
 *
 * This is responsive, below 1200px screen width a hamburger menu is shown and
 * the sidenav isn't visible until the hamburger toggle opens it. At 1200px and
 * above the sidenav is permanently shown. Note that there isn't a 1200px
 * breakpoint in the React app currently, but the AngularJS app does have one.
 * For now we have chosen to keep things consistent and use 1200px as the point
 * the menu changes, in the future we might want to revisit this - probably when
 * we get rid of the AngularJS app altogether.
 */
const Topbar = () => {
  return (
    <Box
      component="header"
      display="flex"
      alignItems="center"
      gap={2}
      paddingX={2}
      sx={{
        // Replace the AngularJS Bootstrap `navbar-fixed-top` in place: fixed to
        // the top, full width, with the same height (52px, 64px at >=1200px,
        // matching loaded-style-overrides.less) and the same z-index as the old
        // navbar (Bootstrap @zindex-navbar-fixed: 1030) so it sits above page
        // content and the sidenav (z-index 999) but BELOW modals (1040/1050),
        // exactly like the navbar it replaces. Page content already clears this
        // via `#page-content-wrapper { padding-top: 50px }` in loaded.less, so
        // no extra offset is needed when the flag is on.
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1030,
        // An explicit 1200px media query rather than a theme breakpoint: the
        // old navbar (and the sidenav CSS that assumes `--topbarHeight: 64px`
        // from 1200px up) switches at exactly 1200px, and this theme's custom
        // breakpoints have no 1200px token (md: 1150, lg: 1536).
        height: '52px',
        '@media (min-width: 1200px)': {
          height: '64px',
        },
        // Restrict the topbar to no wider than the horizontal width of the
        // screen itself. This prevents it becoming scrollable when on a
        // mobile form factor and the page content isn't nicely responsive and
        // ends up triggering a horizontal scroll on the page.
        //
        // Note that we do this only on mobile for now. On Windows, the scrollbar
        // is always present and the 100vw causes the topbar to flow under that
        // which causes the avatar to be partially hidden. It would be best to
        // make the main content region scrollable rather than the whole window,
        // but there are a bunch of AngularJS files that reference window.scroll
        // that would need to be refactored. Given we are gradually replacing all
        // AngularJS pages we'll leave this in place and once we're on a full
        // React app we can make the main content region scrollable.
        ...(isMobile() && { width: '100vw' }),
        backgroundColor: (theme) => theme.app.headerBackground,
        color: (theme) => theme.app.headerTextColor,
      }}
    >
      {/* LEFT region: small-screen menu button + logo + main module nav.
          The mobile button is hidden from 1200px up; the nav is hidden below
          1200px. */}
      <Box
        display="flex"
        alignItems="center"
        // On desktop this gap makes sure the module nav section starts nicely
        // aligned with the left nav / main content edge; on mobile though we
        // need all the space possible and want the padding either side of the
        // hamburger (screen edge to left side of icon, right side of icon to
        // start of logo) to be consistent.
        gap={{ xs: 0, smxl: 2 }}
        flexGrow={1}
        minWidth={0}
        height="100%"
      >
        <TopbarMobileButton />
        <Box
          component="img"
          src={loadedFullNameLogoLatte}
          alt="Loaded"
          sx={{
            // Shift the logo so it visually aligns with the left nav entry
            // padding (26px = 16px topbar padding + 10px here)
            paddingLeft: '10px',
            // On smaller screens we have a slightly smaller logo to make sure
            // there is space for the venue name / and app menu icon.
            height: { xs: '20px', smxl: '30px' },
            // On desktop layouts we need to limit the logo width to make sure
            // we get nice edge alignment with the side name / main content
            // visual line.
            width: { smxl: '198px' },
            objectFit: 'contain',
            objectPosition: 'left center',
            flexShrink: 0,
          }}
        />
        <Box
          sx={{
            // The full module nav shows only from 1200px up; below that the
            // hamburger (TopbarMobileButton) replaces it and the modules are
            // reached via the sidenav popout. Explicit 1200px media query - this
            // theme has no 1200px breakpoint token - matching the hamburger's own
            // switch and the AngularJS desktop sidebar (Bootstrap's `md`/1200px).
            display: 'none',
            '@media (min-width: 1200px)': {
              display: 'flex',
            },
            alignItems: 'center',
            height: '100%',
            minWidth: 0,
          }}
        >
          <TopbarModuleNav />
        </Box>
      </Box>

      {/* RIGHT region: action slots (venue switcher + profile flyout). flexShrink
          0 keeps these from being squeezed; the venue switcher condenses its own
          label on small screens so the region doesn't overflow. */}
      <Box display="flex" alignItems="center" flexShrink={0}>
        <VenueSwitcher />
        <AppMenuFlyout />
      </Box>
    </Box>
  )
}

export default Topbar
